import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

// Ücret bantları (spec 2026-10-27 §5.4, §7.1) — yalnız İK (EM.Write). Şirketin ücret YAPISI; kişi maaşı değil.
export const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];   // backend AtsOfferDataAccess.AllowedCurrencies ile aynı

const listOf = (res) => {
  const r = res.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

const labelOf = (options) => (id) => options.find((o) => o.id === id)?.name || id;

const toNumber = (v) => (v === '' || v == null ? null : Number(v));
const toPayload = (formData) => ({
  gradeId: formData.gradeId,
  jobFamilyId: formData.jobFamilyId || null,
  currency: formData.currency,
  minAmount: toNumber(formData.minAmount),
  midAmount: toNumber(formData.midAmount),
  maxAmount: toNumber(formData.maxAmount),
  // DateTimePicker (date kipi) yerel bileşenlerden "yyyy-MM-dd" üretir; ISO saat dilimi kayması yok.
  validFrom: formData.validFrom ? String(formData.validFrom).slice(0, 10) : null,
  note: formData.note || null,
});

export const formatAmount = (value) => (value == null || value === '' ? '-' : Number(value).toLocaleString('tr-TR'));

const useSalaryBandApi = (queryParams, currentOnly) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [grades, setGrades] = useState([]);
  const [families, setFamilies] = useState([]);
  const lastValidQueryParams = useRef(null);

  // Süzgeç seçenekleri: kademe ve aile listeleri küçüktür (onlar) — bir kez okunur.
  useEffect(() => {
    api.get('/v1/job-grades', { params: { pageNumber: 1, pageSize: 1000 } })
      .then((res) => setGrades(listOf(res).map((g) => ({ id: g.id, name: g.name, level: g.level }))))
      .catch(() => {});
    api.get('/v1/job-families', { params: { pageNumber: 1, pageSize: 1000 } })
      .then((res) => setFamilies(listOf(res).map((f) => ({ id: f.id, name: f.name }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/salary-bands?${new URLSearchParams({ ...paramsToUse, currentOnly: String(currentOnly) })}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, currentOnly, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    try {
      const res = await api.post('/v1/salary-bands', toPayload(formData));
      if (!res.data?.status) return false;
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const update = useCallback(async (id, formData) => {
    try {
      const res = await api.put(`/v1/salary-bands/${id}`, toPayload(formData));
      if (!res.data?.status) return false;
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const _delete = useCallback(async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.delete(`/v1/salary-bands/${id}`)));
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'gradeId', label: t('salaryBand.grade'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: { names: grades.map((g) => g.id), defaultOp: 'eq', renderValue: labelOf(grades) },
        customFilterListOptions: { render: labelOf(grades) },
        customBodyRenderLite: (i) => data[i]?.gradeName || '-',
      },
    },
    {
      name: 'jobFamilyId', label: t('salaryBand.jobFamily'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: { names: families.map((f) => f.id), defaultOp: 'eq', renderValue: labelOf(families) },
        customFilterListOptions: { render: labelOf(families) },
        customBodyRenderLite: (i) => data[i]?.jobFamilyName || t('salaryBand.allFamilies'),
      },
    },
    {
      name: 'currency', label: t('salaryBand.currency'),
      options: { filter: true, sort: false, filterType: 'dropdown', filterOptions: { names: CURRENCIES, defaultOp: 'eq' } },
    },
    { name: 'minAmount', label: t('salaryBand.minAmount'), options: { filter: false, sort: false, customBodyRenderLite: (i) => formatAmount(data[i]?.minAmount) } },
    { name: 'midAmount', label: t('salaryBand.midAmount'), options: { filter: false, sort: false, customBodyRenderLite: (i) => formatAmount(data[i]?.midAmount) } },
    { name: 'maxAmount', label: t('salaryBand.maxAmount'), options: { filter: false, sort: false, customBodyRenderLite: (i) => formatAmount(data[i]?.maxAmount) } },
    {
      name: 'validFrom', label: t('salaryBand.validFrom'),
      options: { filter: false, sort: false, customBodyRenderLite: (i) => (data[i]?.validFrom ? String(data[i].validFrom).slice(0, 10) : '-') },
    },
  ], [t, data, grades, families]);

  const gradeAsync = useMemo(() => ({
    onSearch: async (q) => grades
      .filter((g) => !q || g.name.toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr')))
      .map((g) => ({ id: g.id, name: `${g.name} (${t('jobGrade.levelShort')} ${g.level})` })),
    onGetById: async (id) => {
      const g = grades.find((x) => x.id === id);
      return g ? { id: g.id, name: `${g.name} (${t('jobGrade.levelShort')} ${g.level})` } : { id, name: '' };
    },
  }), [grades, t]);

  const familyAsync = useMemo(() => ({
    onSearch: async (q) => families.filter((f) => !q || f.name.toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr'))),
    onGetById: async (id) => families.find((f) => f.id === id) || { id, name: '' },
  }), [families]);

  const objectSchema = useMemo(() => [
    { name: 'gradeId', label: t('salaryBand.grade'), type: 'asyncSelect', required: true, ...gradeAsync },
    { name: 'jobFamilyId', label: t('salaryBand.jobFamilyOptional'), type: 'asyncSelect', ...familyAsync },
    { name: 'currency', label: t('salaryBand.currency'), type: 'select', required: true, options: CURRENCIES.map((c) => ({ id: c, name: c })) },
    { name: 'minAmount', label: t('salaryBand.minAmount'), type: 'number', required: true, min: 0, step: 0.01 },
    { name: 'midAmount', label: t('salaryBand.midAmount'), type: 'number', min: 0, step: 0.01 },
    { name: 'maxAmount', label: t('salaryBand.maxAmount'), type: 'number', required: true, min: 0, step: 0.01 },
    { name: 'validFrom', label: t('salaryBand.validFrom'), type: 'date', required: true },
    { name: 'note', label: t('salaryBand.note'), type: 'textarea' },
  ], [t, gradeAsync, familyAsync]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useSalaryBandApi;
